import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { getSingaleData } from "../../../Model/SinglePage";

const BELL_MT = '"Bell MT", Georgia, "Times New Roman", serif';
const PLACEHOLDER = "https://placehold.co/500x500/EEE/31343C?text=No+Image";

/**
 * Other lots from the SAME auction as the current lot (never another auction).
 * Uses the existing single-lot API with the current auction id (`lotID`) and nearby lot
 * numbers, keeps only results whose auction_id matches, and excludes the current lot.
 * Clicking a card calls `onSelectLot(lotNumber, cleanTitle)`; the page loads the lot.
 */
export default function RelatedLots({ auctionId, currentLotNumber, totalLots, lotID, onSelectLot }) {
  const [lots, setLots] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const total = Number(totalLots) || 0;
    const current = Number(currentLotNumber) || 0;
    if (!lotID || !current || !auctionId) {
      setLots([]);
      return undefined;
    }

    // Only fetch nearby lots from the current auction/set. Never mix another auction.
    const offsets = [-3, -2, -1, 1, 2, 3];
    const targets = offsets.map((offset) => current + offset).filter((number) => number >= 1 && (!total || number <= total));

    Promise.all(
      targets.map(async (lotNumber) => {
        try {
          const result = await getSingaleData(lotID, lotNumber);
          const data = result?.msg?.data;
          if (!data || Number(data.auction_id) !== Number(auctionId)) return null;
          if (Number(data.lot_number) === current) return null;
          const image = result?.msg?.image?.[0]?.img_url || PLACEHOLDER;
          return {
            id: data.id,
            lot_number: data.lot_number,
            title: data.title || "Untitled",
            artist: data.artist || "",
            image,
          };
        } catch (error) {
          return null;
        }
      })
    ).then((results) => {
      if (!cancelled) setLots(results.filter(Boolean));
    });

    return () => { cancelled = true; };
  }, [auctionId, currentLotNumber, totalLots, lotID]);

  if (!lots.length) return null;

  return (
    <section className="related-lots-section" aria-labelledby="related-lots-heading">
      <Box sx={{ mt: { xs: 5, md: 7 }, pt: 4, borderTop: "1px solid #e9e9e9" }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography id="related-lots-heading" sx={{ fontFamily: BELL_MT, fontSize: { xs: 22, md: 27 }, fontWeight: 400, color: "#111" }}>
            Related Lots in This Auction
          </Typography>
          <Typography sx={{ mt: .5, fontFamily: BELL_MT, fontSize: 14, color: "#777" }}>
            Other lots from the same auction.
          </Typography>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", md: "repeat(6, minmax(0, 1fr))" }, gap: { xs: 1.5, md: 2 } }}>
          {lots.map((lot) => {
            const cleanTitle = String(lot.title).replace(/[^a-zA-Z0-9-_]/g, "") || "untitled";
            return (
              <Box
                key={`${lot.id}-${lot.lot_number}`}
                component="button"
                type="button"
                onClick={() => onSelectLot(lot.lot_number, cleanTitle)}
                sx={{ border: 0, bgcolor: "transparent", p: 0, textAlign: "left", cursor: "pointer", minWidth: 0, fontFamily: BELL_MT, "&:hover .related-lot-image": { transform: "scale(1.03)" } }}
              >
                <Box sx={{ overflow: "hidden", aspectRatio: "1 / 1", bgcolor: "#f7f7f7", border: "1px solid #eee" }}>
                  <Box className="related-lot-image" component="img" src={lot.image} alt={lot.title} sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block", transition: "transform .25s ease" }} />
                </Box>
                <Typography sx={{ mt: 1, fontFamily: BELL_MT, fontSize: 11, letterSpacing: "1px", color: "#888", textTransform: "uppercase" }}>
                  Lot {lot.lot_number}
                </Typography>
                <Typography sx={{ mt: .25, fontFamily: BELL_MT, fontSize: 15, lineHeight: 1.25, color: "#111", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {lot.title}
                </Typography>
                {lot.artist && <Typography sx={{ mt: .25, fontFamily: BELL_MT, fontSize: 12, color: "#777", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} dangerouslySetInnerHTML={{ __html: lot.artist }} />}
                <ArrowForwardIosIcon sx={{ mt: .75, fontSize: 11, color: "#777" }} />
              </Box>
            );
          })}
        </Box>
      </Box>
    </section>
  );
}
